/**
 * XoeMark — the Xoe AI brand mark.
 *
 * A legible "X" monogram with a small sparkle accent that signals "AI" without
 * the toy-robot connotation of the old RobotOutlined glyph. Purely
 * presentational: it renders no data, owns no logic, and exposes no behavioural
 * contract — colour is inherited from the parent via `currentColor`, all sizing
 * derives from the `size` prop, and motion uses the `--mg-*` tokens.
 *
 * The optional idle shimmer plays only on the sparkle accent (a faint ambient
 * "thinking" cue) and is disabled under `prefers-reduced-motion`.
 */

import React from 'react';
import { RiSparkling2Fill } from 'react-icons/ri';
import type { XoeMarkProps } from '../../types/xoe/XoeUiTypes';

const XoeMark: React.FC<XoeMarkProps> = ({ size = 22, animated = true, showMonogram = true }) => {
	const sparkSize = showMonogram ? Math.round(size * 0.5) : Math.round(size * 0.86);

	return (
		<span
			aria-hidden="true"
			style={{
				position: 'relative',
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: size,
				height: size,
				lineHeight: 1,
				color: 'currentColor',
			}}
		>
			{/* Monogram base — legible, brand-forward */}
			{showMonogram && (
				<span
					style={{
						fontFamily: 'var(--mg-font-sans)',
						fontSize: Math.round(size * 0.92),
						fontWeight: 700,
						letterSpacing: '-0.04em',
					}}
				>
					X
				</span>
			)}

			{/* AI sparkle — corner accent on the monogram, or centred on its own */}
			<RiSparkling2Fill
				className={animated ? 'mg-xoe-spark' : undefined}
				style={
					showMonogram
						? {
								position: 'absolute',
								top: -Math.round(size * 0.06),
								right: -Math.round(size * 0.1),
								fontSize: sparkSize,
						  }
						: { fontSize: sparkSize }
				}
			/>

			{animated && (
				<style>{`
					@keyframes mg-xoe-spark-shimmer {
						0%, 100% { opacity: 0.7; transform: scale(0.92); }
						50%      { opacity: 1;   transform: scale(1.08); }
					}
					.mg-xoe-spark {
						transform-origin: center;
						animation: mg-xoe-spark-shimmer calc(var(--mg-dur-slow) * 8) ease-in-out infinite;
					}
					@media (prefers-reduced-motion: reduce) {
						.mg-xoe-spark { animation: none; }
					}
				`}</style>
			)}
		</span>
	);
};

export default XoeMark;
